import { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListSubheader,
    Menu,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SearchIcon from '@mui/icons-material/Search';

// Components
import { Page, ControlBarHeader } from 'components/ui/layout';
import BundleCreateModal from 'components/bundles/BundleCreateModal';
import BundleEditModal from 'components/bundles/BundleEditModal';
import ProductCreator from 'components/forms/ProductCreator';
import ProductEditor from 'components/forms/ProductEditor';
import DisplayOrderModal from 'components/products/DisplayOrderModal';
import FullScreenModal from 'components/popups/FullScreenModal';
import BundleList from 'pages/event/products/bundles/Bundles';
import ProductList from 'pages/event/products/admission/Admission';
import Seats from 'pages/event/products/seats/Seats';
import SeatingChart from 'components/SeatingChart';
import ProductGroups from './ProductGroups';

// Utility
import { useAuth } from 'context/auth';
import { useEvent } from 'context/event';
import { useNotification } from 'context/notification';
import {
    getAdmissionPageProducts,
    updateAddonSort,
    updateAdmissionSort,
} from './admission/AdmissionAPI';
import { getBundles, updateBundleSort } from './bundles/BundlesAPI';
import { getSeatCategories, getSeatProducts } from './seats/SeatsAPI';
import { getProductFilter, PRODUCT_FILTER_CATEGORIES, PRODUCT_FILTERS } from './productFilters';

const PRODUCT_TYPES = {
    admission: 1,
    addon: 2,
    seat: 3,
};

const PRODUCT_MODAL_TITLES = {
    [PRODUCT_TYPES.admission]: {
        create: 'Create Ticket',
        copy: 'Copy Ticket',
        edit: 'Edit Ticket',
    },
    [PRODUCT_TYPES.addon]: {
        create: 'Create Add-on',
        copy: 'Copy Add-on',
        edit: 'Edit Add-on',
    },
    [PRODUCT_TYPES.seat]: {
        create: 'New Seat Price',
        edit: 'Edit Seat Price',
    },
};

export default function Products(props) {
    const eventId = props.match.params.id;

    const event = useEvent();
    const { auth } = useAuth();
    const { notify } = useNotification();
    const history = useHistory();
    const seatsEnabled = event?.seats_flag === 1;
    const hostId = auth.hostId;

    const [products, setProducts] = useState([]);
    const [productsStatus, setProductsStatus] = useState('loading');
    const [productsRefresh, setProductsRefresh] = useState(0);
    const [bundles, setBundles] = useState([]);
    const [bundlesStatus, setBundlesStatus] = useState('loading');
    const [bundlesRefresh, setBundlesRefresh] = useState(0);
    const [seatCategories, setSeatCategories] = useState([]);
    const [seatProducts, setSeatProducts] = useState([]);
    const [seatStatus, setSeatStatus] = useState('loading');
    const [seatRefresh, setSeatRefresh] = useState(0);
    const [attendeeTicketNotice, setAttendeeTicketNotice] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [compact, setCompact] = useState(false);
    const [selectedFilterValue, setSelectedFilterValue] = useState('all');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);
    const [ticketGroupsOpen, setTicketGroupsOpen] = useState(false);
    const [productModal, setProductModal] = useState({
        open: false,
        productId: null,
        productType: null,
        mode: null,
    });
    const [bundleModal, setBundleModal] = useState({
        open: false,
        bundleId: null,
        mode: null,
    });
    const [reorder, setReorder] = useState(null);
    const [reorderLoading, setReorderLoading] = useState(false);

    useEffect(() => {
        setProductsStatus('loading');

        getAdmissionPageProducts(eventId)
            .then((fetchedProducts) => {
                setProducts(fetchedProducts);
                setProductsStatus('success');
            })
            .catch(() => setProductsStatus('error'));
    }, [eventId, productsRefresh]);

    useEffect(() => {
        if (seatsEnabled) {
            return;
        }

        setBundlesStatus('loading');

        getBundles(eventId)
            .then((fetchedBundles) => {
                setBundles(fetchedBundles);
                setBundlesStatus('success');
            })
            .catch(() => setBundlesStatus('error'));
    }, [eventId, bundlesRefresh, seatsEnabled]);

    useEffect(() => {
        if (!seatsEnabled) {
            return;
        }

        setSeatStatus('loading');

        Promise.all([getSeatCategories(eventId), getSeatProducts(eventId)])
            .then(([categories, products]) => {
                setSeatCategories(categories);
                setSeatProducts(products);
                setSeatStatus('success');
            })
            .catch(() => setSeatStatus('error'));
    }, [eventId, seatsEnabled, seatRefresh]);

    const refreshProducts = () => setProductsRefresh((value) => value + 1);
    const refreshBundles = () => setBundlesRefresh((value) => value + 1);
    const refreshSeats = () => setSeatRefresh((value) => value + 1);

    const handleProductCreated = (product) => {
        if (product?.isAttendee) {
            setAttendeeTicketNotice({ productName: product.name });
        }
    };

    const handleProductEdited = (product) => {
        if (product?.previousIsAttendee === false && product?.isAttendee === true) {
            setAttendeeTicketNotice({ productName: product.name });
        }
    };

    const handleFilterMenuOpen = (event) => setFilterAnchorEl(event.currentTarget);
    const handleFilterMenuClose = () => setFilterAnchorEl(null);
    const handleCreateMenuOpen = (event) => setCreateMenuAnchorEl(event.currentTarget);
    const handleCreateMenuClose = () => setCreateMenuAnchorEl(null);

    const handleFilterChange = (filter) => {
        setSelectedFilterValue(filter.value);
        handleFilterMenuClose();
    };

    const openProductModal = (mode, productType, productId = null) => {
        setProductModal({ open: true, productId, productType, mode });
    };

    const handleProductModalClose = () => {
        setProductModal({ open: false, productId: null, productType: null, mode: null });
    };

    const handleCreateProduct = (productType) => {
        handleCreateMenuClose();
        openProductModal('create', productType);
    };

    const handleProductCreate = (createdProduct) => {
        handleProductModalClose();
        if (productModal.productType === PRODUCT_TYPES.seat) {
            refreshSeats();
        } else {
            refreshProducts();
            handleProductCreated(createdProduct);
        }
    };

    const handleProductEdit = (updatedProduct) => {
        handleProductModalClose();
        if (productModal.productType === PRODUCT_TYPES.seat) {
            refreshSeats();
        } else {
            refreshProducts();
            handleProductEdited(updatedProduct);
        }
    };

    const handleProductDelete = () => {
        handleProductModalClose();
        if (productModal.productType === PRODUCT_TYPES.seat) {
            refreshSeats();
        } else {
            refreshProducts();
        }
    };

    const handleCreateBundle = () => {
        handleCreateMenuClose();
        setBundleModal({ open: true, bundleId: null, mode: 'create' });
    };

    const handleBundleEditOpen = (bundleId) => {
        setBundleModal({ open: true, bundleId, mode: 'edit' });
    };

    const handleBundleModalClose = () => {
        setBundleModal({ open: false, bundleId: null, mode: null });
    };

    const handleBundleCreate = () => {
        handleBundleModalClose();
        refreshBundles();
    };

    const handleBundleEdit = () => {
        handleBundleModalClose();
        refreshBundles();
    };

    const handleReorderOpen = (type) => setReorder({ type });

    const handleReorderClose = () => {
        if (!reorderLoading) {
            setReorder(null);
        }
    };

    const handleReorderSubmit = (sort) => {
        if (!reorder) {
            return;
        }

        setReorderLoading(true);

        const saveOrder = {
            admission: () => updateAdmissionSort(eventId, sort),
            addon: () => updateAddonSort(eventId, sort),
            bundle: () => updateBundleSort(eventId, sort),
        }[reorder.type];

        saveOrder()
            .then(() => {
                if (reorder.type === 'bundle') {
                    refreshBundles();
                } else {
                    refreshProducts();
                }
                setReorder(null);
                notify.success('Saved Order');
            })
            .catch(() => {
                notify.error('Unable to change order');
            })
            .finally(() => {
                setReorderLoading(false);
            });
    };

    const selectedFilter = getProductFilter(selectedFilterValue);
    const reorderItems =
        reorder?.type === 'bundle'
            ? bundles
            : products.filter((product) => product.prod_type_id === PRODUCT_TYPES[reorder?.type]);
    const productModalTitle =
        PRODUCT_MODAL_TITLES[productModal.productType]?.[productModal.mode] ?? '';

    return (
        <Page>
            <Box
                mb={4}
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <ControlBarHeader>Tickets</ControlBarHeader>
                </Box>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <Button
                        variant="contained"
                        endIcon={<ArrowDropDownIcon />}
                        onClick={handleCreateMenuOpen}
                    >
                        New Ticket Type
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<FolderOutlinedIcon />}
                        onClick={() => setTicketGroupsOpen(true)}
                    >
                        Manage Ticket Groups
                    </Button>
                </Box>
            </Box>
            {attendeeTicketNotice && (
                <Box mb={4}>
                    <Alert
                        severity="warning"
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => history.push(`/events/${eventId}/checkout-form`)}
                            >
                                Review
                            </Button>
                        }
                        onClose={() => setAttendeeTicketNotice(null)}
                    >
                        <AlertTitle>
                            Review checkout questions for {attendeeTicketNotice.productName}
                        </AlertTitle>
                        <Typography variant="body2">
                            Ticket-specific questions aren not automatically included for new
                            attendee tickets.
                        </Typography>
                    </Alert>
                </Box>
            )}

            {!seatsEnabled ? (
                <Paper sx={{ p: 1.5, mb: 4 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={2}
                    >
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                endAdornment: (
                                    <IconButton
                                        aria-label="Clear product search"
                                        size="small"
                                        sx={{ visibility: searchText ? 'visible' : 'hidden' }}
                                        onClick={() => setSearchText('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                            inputProps={{ 'aria-label': 'Search products by name' }}
                            sx={{ width: '360px' }}
                        />
                        <Box display="flex" alignItems="center" gap={2}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterListIcon />}
                                onClick={handleFilterMenuOpen}
                            >
                                {selectedFilter.label}
                            </Button>
                            <FormControlLabel
                                label="Compact"
                                control={
                                    <Switch
                                        checked={compact}
                                        onChange={(event) => setCompact(event.target.checked)}
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </Paper>
            ) : null}

            <Menu
                anchorEl={createMenuAnchorEl}
                open={Boolean(createMenuAnchorEl)}
                onClose={handleCreateMenuClose}
            >
                {!seatsEnabled && (
                    <MenuItem onClick={() => handleCreateProduct(PRODUCT_TYPES.admission)}>
                        <ListItemIcon>
                            <ConfirmationNumberIcon fontSize="small" color='info' />
                        </ListItemIcon>
                        Admission Ticket
                    </MenuItem>
                )}
                {seatsEnabled && (
                    <MenuItem onClick={() => handleCreateProduct(PRODUCT_TYPES.seat)}>
                        Seat Ticket Price
                    </MenuItem>
                )}
                <MenuItem onClick={() => handleCreateProduct(PRODUCT_TYPES.addon)}>
                    <ListItemIcon>
                        <AddShoppingCartIcon fontSize="small" color='info' />
                    </ListItemIcon>
                    Add-On Ticket
                </MenuItem>
                {!seatsEnabled && (
                    <MenuItem onClick={handleCreateBundle}>
                        <ListItemIcon>
                            <LocalMallIcon fontSize="small" color='info' />
                        </ListItemIcon>
                        Bundle
                    </MenuItem>
                )}
            </Menu>

            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterMenuClose}
            >
                {PRODUCT_FILTER_CATEGORIES.map((category) => (
                    <Fragment key={category.value}>
                        <ListSubheader>{category.label}</ListSubheader>
                        {PRODUCT_FILTERS.filter((filter) => filter.category === category.value).map(
                            (filter) => (
                                <FilterMenuItem
                                    key={filter.value}
                                    checked={selectedFilter.value === filter.value}
                                    onClick={() => handleFilterChange(filter)}
                                >
                                    {filter.menuLabel}
                                </FilterMenuItem>
                            ),
                        )}
                    </Fragment>
                ))}
            </Menu>

            <ProductGroups
                eventId={eventId}
                open={ticketGroupsOpen}
                onClose={() => setTicketGroupsOpen(false)}
            />

            {seatsEnabled ? (
                <>
                    <Box mb={8}>
                        <SeatingChart
                            eventId={eventId}
                            modes={
                                event.seats_closed_at
                                    ? ['static']
                                    : ['static', 'manageForSaleConfig']
                            }
                        />
                        <Typography mt={1} textAlign={'center'}>
                            Seats are colour coded to match the seating categories below
                        </Typography>
                    </Box>
                    <Seats
                        status={seatStatus}
                        categories={seatCategories}
                        products={seatProducts}
                        onProductEdit={(productId) =>
                            openProductModal('edit', PRODUCT_TYPES.seat, productId)
                        }
                    />

                    <ProductList
                        event={event}
                        status={productsStatus}
                        products={products}
                        displayProductTypes={[PRODUCT_TYPES.addon]}
                        searchText={searchText}
                        selectedFilter={selectedFilter}
                        onProductEdit={(id, type) =>
                            openProductModal('edit', PRODUCT_TYPES[type], id)
                        }
                        onProductCopy={(id, type) =>
                            openProductModal('copy', PRODUCT_TYPES[type], id)
                        }
                        onOpenReorder={handleReorderOpen}
                    />
                </>
            ) : (
                <>
                    <ProductList
                        event={event}
                        status={productsStatus}
                        products={products}
                        displayProductTypes={[PRODUCT_TYPES.admission, PRODUCT_TYPES.addon]}
                        searchText={searchText}
                        selectedFilter={selectedFilter}
                        compact={compact}
                        onProductEdit={(id, type) =>
                            openProductModal('edit', PRODUCT_TYPES[type], id)
                        }
                        onProductCopy={(id, type) =>
                            openProductModal('copy', PRODUCT_TYPES[type], id)
                        }
                        onOpenReorder={handleReorderOpen}
                    />

                    <BundleList
                        event={event}
                        status={bundlesStatus}
                        bundles={bundles}
                        searchText={searchText}
                        selectedFilter={selectedFilter}
                        compact={compact}
                        onBundleEdit={handleBundleEditOpen}
                        onOpenReorder={() => handleReorderOpen('bundle')}
                    />
                </>
            )}

            <DisplayOrderModal
                open={Boolean(reorder)}
                items={reorderItems}
                description={`Drag and drop ${reorder?.type === 'bundle' ? 'bundles' : 'tickets'} to change their order on the purchase page.`}
                getItemId={(item) =>
                    reorder?.type === 'bundle' ? item.bundle_id : item.product_id
                }
                getItemLabel={(item) =>
                    reorder?.type === 'bundle' ? item.bundle_name : item.prod_name
                }
                onClose={handleReorderClose}
                onSave={handleReorderSubmit}
                loading={reorderLoading}
            />

            <FullScreenModal
                open={productModal.open}
                onClose={handleProductModalClose}
                title={productModalTitle}
                cancelText="Cancel"
            >
                {(productModal.mode === 'create' || productModal.mode === 'copy') && (
                    <ProductCreator
                        productType={productModal.productType}
                        hostId={hostId}
                        eventId={eventId}
                        productId={
                            productModal.mode === 'copy' ? productModal.productId : undefined
                        }
                        onCreate={handleProductCreate}
                        onCancel={handleProductModalClose}
                    />
                )}

                {productModal.mode === 'edit' && (
                    <ProductEditor
                        productType={productModal.productType}
                        hostId={hostId}
                        eventId={eventId}
                        productId={productModal.productId}
                        onCancel={handleProductModalClose}
                        onEdit={handleProductEdit}
                        onDelete={handleProductDelete}
                    />
                )}
            </FullScreenModal>

            <BundleCreateModal
                open={bundleModal.open && bundleModal.mode === 'create'}
                eventId={eventId}
                onCreate={handleBundleCreate}
                onClose={handleBundleModalClose}
            />

            <BundleEditModal
                open={bundleModal.open && bundleModal.mode === 'edit'}
                eventId={eventId}
                bundleId={bundleModal.bundleId}
                onUpdate={handleBundleEdit}
                onClose={handleBundleModalClose}
            />
        </Page>
    );
}

function FilterMenuItem({ checked, children, ...props }) {
    return (
        <MenuItem selected={checked} {...props}>
            <ListItemIcon>{checked && <CheckIcon fontSize="small" />}</ListItemIcon>
            {children}
        </MenuItem>
    );
}
